import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Box, Stack, Typography, Grid } from "@mui/material";
import { throttleTime } from "@/global/utils/utils";
import { EventType } from "@/global/store/data/types";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { CommonEventCard } from "@/shared/common/CommonEventCard";
import { useAppStore } from "@/global/store";
import { selectEvents } from "@/global/store/data/purchaseSeats/events";
import { useAutoSeatSelectionState } from "./useAutoSeatSelectionState";

export interface ModalResponse {
  action: "manualSelect";
}

export const useModalState = createState({
  isOpen: false,
  eventCodes: [] as string[],
  onFinish: (a: ModalResponse) => {},
});

export const useAutoSeatSelectionNoInventoryAlertModal = () => {
  const { isOpen, onFinish, set, reset, ...modalState } = useModalState();
  const execute = (args: typeof modalState) => {
    return new Promise<ModalResponse>((resolve) => {
      set({ ...args, isOpen: true, onFinish: resolve });
    });
  };
  return { ...modalState, isOpen, onFinish, set, reset, execute };
};

export function AutoSeatSelectionNoInventoryAlertModal() {
  const { md: mdDown } = useCssMediaQuery("down");
  const autoSeatSelectionState = useAutoSeatSelectionState();
  const modalState = useAutoSeatSelectionNoInventoryAlertModal();
  const events = useAppStore(selectEvents);
  const eventsToDisplay = events.filter((evt) => {
    return modalState.eventCodes.includes(evt.eventCode);
  });

  const handleClose = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => modalState.reset());
  };

  const onClickSelectSeatsManually = () => {
    autoSeatSelectionState.set({ mode: "manualSelect" });
    modalState.onFinish({ action: "manualSelect" });
    handleClose();
  };

  return (
    <>
      <NotificationModal
        isOpen={modalState.isOpen}
        hideTitleIcon
        hideCloseIcon={true}
        secondaryButtonText={"Continue"}
        primaryButtonText="Choose Another Event"
        onClose={handleClose}
        onClickSecondaryButton={onClickSelectSeatsManually}
        title="Manual Seat Selection Needed"
        description={
          <Box>
            <Box>
              <Typography variant="body1">
                We couldn’t auto-assign seats for the following event(s) due to
                limited availability. Please select your seats manually to
                continue.
              </Typography>
            </Box>
            <Grid container spacing={2} mt={1}>
              {eventsToDisplay.map((evt) => {
                return (
                  <Grid item xs={12} key={evt.eventCode}>
                    <CommonEventCard event={evt} />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        }
        renderFooter={() => {
          return (
            <Stack
              p={2}
              direction={mdDown ? "column" : "row"}
              justifyContent="flex-end"
              spacing={2}
            >
              <ModalActionButton
                variant="primary"
                onClick={onClickSelectSeatsManually}
              >
                Select Seats Manually
              </ModalActionButton>
            </Stack>
          );
        }}
      />
    </>
  );
}
